import { useMe } from '../session/SessionProvider';
import { useVisitorAccess } from '../visitors/shared';
import { useIsOverseer, useVerifiedDenomination } from '../denomination/denomination';
import { useHomeEnabled } from '../hogar/hogar';
import { useLiveConfigured } from './menuData';

export interface NavItem {
  to: string;
  label: string;
  /** Etiqueta corta para la barra inferior del celular. */
  short: string;
  icon: string;
  /** Otras rutas que pertenecen a esta opción (p. ej. Agenda incluye Eventos, Reuniones y Clases). */
  also?: string[];
}

/** Una línea que explica cada opción en las páginas de sección. */
export const HINTS: Record<string, string> = {
  '/app/ministerios': 'Departamentos, líderes y organigrama.',
  '/app/avisos': 'Anuncios para toda la iglesia.',
  '/app/miembros': 'Directorio, familias, solicitudes y visitas.',
  '/app/vocacion': 'Vacantes de los ministerios y dónde servir.',
  '/app/nuestra-historia': 'Fundación, misión, visión y pastores.',
  '/app/consejeria': 'Pide o da acompañamiento pastoral.',
  '/app/mantenimiento': 'Reportes y arreglos del templo.',
  '/app/ordenanzas': 'Inscríbete para bautizarte.',
  '/app/ordenanzas?ver=presentaciones': 'Presenta a tu hijo o hija ante la iglesia.',
  '/app/salones': 'Tu salón, tus clases y tus tareas.',
  '/app/cultos': 'Los cultos de la semana y su programa.',
  '/app/calendario': 'Eventos, reuniones y campamentos.',
  '/app/chats': 'Conversa con tus ministerios.',
  '/app/oracion': 'Pide oración y ora por otros.',
  '/app/lideres': 'Ministerios y miembros.',
  '/app/organigrama': 'Pastoral, ministerios y sus líderes, para ver e imprimir.',
  '/app/guarderia': 'Entrada y salida de los niños.',
  '/app/hogar': 'Tareas, calendario, muro y juegos de tu familia en casa.',
  '/app/testimonios': 'Muro de gratitud y lo que Dios ha hecho.',
  '/app/foro': 'Conversaciones de la iglesia.',
  '/app/encuestas': 'Opina y vota.',
  '/app/ayuda': 'Pide o ofrece ayuda a los hermanos.',
  '/app/misiones': 'Misioneros y proyectos.',
  '/app/red': 'Otras iglesias conectadas.',
  '/app/campanas': 'Ayunos y campañas de oración o lectura.',
  '/app/monedero': 'Mi saldo para la cafetería y la tienda.',
  '/app/recaudaciones': 'Ventas y colectas para una causa.',
  '/app/llamado': 'Curso para nuevos creyentes, paso a paso.',
  '/app/biblia': 'Leer, memorizar, trivia, mapa, personajes y diccionario.',
  '/app/devocional': 'Planes para leer la Biblia cada día.',
  '/app/crecimiento': 'Mi avance y mis metas espirituales.',
  '/app/cuaderno': 'Mis notas de estudio.',
  '/app/oracion/diario': 'Mis peticiones y respuestas.',
  '/app/biblioteca': 'Libros y recursos de la iglesia.',
  '/app/publicaciones': 'Predicaciones y estudios publicados.',
  '/app/cantos': 'Letras, tono y velocidad de cada canto.',
  '/app/contenido': 'Escribe predicaciones y estudios.',
  '/app/multimedia': 'Fotos, videos y archivos.',
  '/app/presentaciones': 'Proyecta cantos, versículos y textos.',
  '/app/formularios': 'Inscripciones y formularios con varias preguntas.',
  '/app/asambleas/credenciales': 'Exhortadores, licenciados y ordenados, con su vencimiento.',
  '/app/asambleas/ofrendas': 'Misiones, distrito y EBV: recibido, enviado y por enviar.',
  '/app/asambleas/escuela-dominical': 'Clases por edad y el registro de cada domingo.',
  '/app/asambleas/ebv': 'Inscripción, grupos, lista diaria y quién aceptó a Cristo.',
  '/app/asambleas/exploradores': 'Ramas por edad, insignias, rangos y avance de cada niño.',
  '/app/asambleas/misioneritas': 'Grupos por edad, insignias, rangos y avance de cada niña.',
  '/app/biblia/trivia': 'Por equipos y por libro: proyecta y cada quien contesta desde su celular.',
  '/app/asambleas/presbiterio': 'Para presbíteros: el resumen de las iglesias a tu cargo.',
  '/app/asambleas/ministerios': 'Femenil, Varones, Embajadores, Exploradores, Misioneritas y más.',
  '/app/asambleas/informe': 'Para el distrito: miembros, convertidos, bautizados y asistencia.',
  '/app/aprobaciones': 'Lo que espera tu visto bueno.',
  '/app/finanzas': 'Ofrendas, diezmos y gastos.',
  '/app/promesas': 'Lo que prometiste dar y cada entrega.',
  '/app/inventario': 'Equipo y materiales.',
  '/app/comercio': 'Caja, pedidos y catálogo de la cafetería o la tienda.',
  '/app/reportes': 'Números de la iglesia.',
  '/app/configuracion-iglesia': 'Denominación, en vivo, Mercado Pago y recordatorios.',
  '/app/sitio': 'La página pública de la iglesia.',
  '/app/en-vivo': 'La transmisión de tu iglesia, aquí mismo.',
  '/app/facturacion': 'Tu plan de KEORA.',
  '/app/admin/iglesias': 'Todas las iglesias de KEORA.',
  '/app/admin/usuarios': 'Todas las cuentas: correo, contraseña y estado.',
  '/app/planes': 'Planes y precios.',
};

/** Secciones que en el menú se vuelven una sola opción con su página de accesos. */
export const HUBS: { title: string; to: string; short: string; icon: string; subtitle: string }[] = [
  { title: 'Mi iglesia', to: '/app/mi-iglesia', short: 'Mi iglesia', icon: 'church', subtitle: 'Cultos, Raíces, agenda, chats, oración y tus ministerios.' },
  { title: 'Palabra', to: '/app/palabra', short: 'Palabra', icon: 'bible', subtitle: 'Biblia, lectura y crecimiento.' },
  { title: 'Comunidad', to: '/app/comunidad', short: 'Comunidad', icon: 'people', subtitle: 'Gratitud y testimonios, foro, ayuda, recaudaciones y misiones.' },
  { title: 'Dirigir', to: '/app/lideres', short: 'Dirigir', icon: 'people', subtitle: 'Ministerios y miembros de la iglesia.' },
  { title: 'Culto y medios', to: '/app/herramientas', short: 'Medios', icon: 'music', subtitle: 'Cantos, contenido, multimedia, proyectar y formularios.' },
  { title: 'Asambleas de Dios', to: '/app/asambleas', short: 'AD', icon: 'shield', subtitle: 'Herramientas de las Asambleas de Dios para tu iglesia.' },
  { title: 'Administración', to: '/app/administracion', short: 'Admin', icon: 'chart', subtitle: 'Finanzas, reportes, sitio y plan.' },
  { title: 'Panel KEORA', to: '/app/panel', short: 'Panel', icon: 'globe', subtitle: 'Administración de la plataforma.' },
];

/** Comunidad vive dentro de Mi iglesia: no va como sección aparte en el menú. */
const INSIDE_MY_CHURCH = ['Comunidad'];

/**
 * Globito de una opción del menú (o de varias juntas, como «Más»): lo pendiente en Miembros y
 * Aprobaciones y los mensajes sin leer de Chats. Las secciones suman lo suyo.
 */
export function badgeFor(item: NavItem | NavItem[], badges: { members: number; approvals: number; chats?: number }): number {
  const routes = (Array.isArray(item) ? item : [item]).flatMap((i) => [i.to, ...(i.also ?? [])]);
  let n = 0;
  // Aprobaciones ya incluye las solicitudes para unirse: no se cuentan dos veces.
  if (routes.includes('/app/aprobaciones')) n += badges.approvals;
  else if (routes.includes('/app/miembros')) n += badges.members;
  if (routes.includes('/app/chats')) n += badges.chats ?? 0;
  return n;
}

/** Grupos con título dentro de la página de una sección (lo que no entra en un grupo va al final). */
export const HUB_GROUPS: Record<string, { title: string; routes: string[] }[]> = {
  'Asambleas de Dios': [
    { title: 'Informes y registros', routes: ['/app/asambleas/informe', '/app/asambleas/credenciales', '/app/asambleas/ofrendas', '/app/asambleas/presbiterio'] },
    {
      title: 'Ministerios',
      routes: ['/app/asambleas/escuela-dominical', '/app/asambleas/ebv', '/app/asambleas/exploradores', '/app/asambleas/misioneritas', '/app/biblia/trivia', '/app/asambleas/ministerios'],
    },
  ],
  Administración: [
    { title: 'Dinero', routes: ['/app/aprobaciones', '/app/finanzas', '/app/comercio', '/app/inventario', '/app/reportes'] },
    { title: 'Configurar', routes: ['/app/configuracion-iglesia', '/app/sitio', '/app/facturacion'] },
  ],
};

/** Nombres propios que no se escriben en minúscula dentro de una frase. */
const KEEP_CASE = /^(Biblia|Raíces|EBV|Misioneritas|Exploradores|Escuela|Mi sección|Ministerios del Concilio)/;

/** Descripción de una sección con lo que esa persona sí ve: «Recaudaciones y formularios». */
export function describeItems(items: NavItem[]): string {
  const names = items.map((i, n) => (n === 0 || KEEP_CASE.test(i.label) ? i.label : i.label.charAt(0).toLowerCase() + i.label.slice(1)));
  if (names.length === 0) return '';
  const shown = names.length > 4 ? [...names.slice(0, 4), 'más'] : names;
  return `${shown.length > 1 ? `${shown.slice(0, -1).join(', ')} y ${shown[shown.length - 1]}` : shown[0]}.`;
}

/** ¿La ruta actual pertenece a la opción? */
export function isActive(item: NavItem, pathname: string): boolean {
  if (item.to === '/app') return pathname === '/app';
  return [item.to, ...(item.also ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const ICONS: Record<string, string> = {
  share: 'M12 15V3M8 7l4-4 4 4M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7',
  pin: 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  wrench: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z',
  heart: 'M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z',
  home: 'M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  family: 'M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM12 18s-3.5-2.2-3.5-4.7a1.9 1.9 0 0 1 3.5-1 1.9 1.9 0 0 1 3.5 1C15.5 15.8 12 18 12 18z',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  worship: 'M12 2v5M9.5 4.5h5M4 21V11.5l8-4 8 4V21M2 21h20M10 21v-4a2 2 0 0 1 4 0v4',
  inbox: 'M4 4h16v10h-5l-1.5 3h-3L9 14H4zM4 14v6h16v-6',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-8 9a8 8 0 0 1 16 0',
  plus: 'M12 5v14M5 12h14',
  card: 'M3 6h18v12H3zM3 10h18M7 15h4',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  pray: 'M12 3c-1 3-4 5-4 9v6l-4 3h8M12 3c1 3 4 5 4 9v6l4 3h-8',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0',
  megaphone: 'M3 10v4h3l7 4V6L6 10zM16 9a4 4 0 0 1 0 6',
  form: 'M6 3h9l3 3v15H6zM9 9h6M9 13h6M9 17h4',
  coin: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM15 9h-4.5a1.5 1.5 0 0 0 0 3h3a1.5 1.5 0 0 1 0 3H9M12 7v2M12 15v2',
  box: 'M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5l9 4.5 9-4.5M12 12v9',
  cup: 'M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5zM17 10h1.5a2.5 2.5 0 0 1 0 5H17M8 3v2M12 3v2',
  book: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM4 21V5M8 7h7',
  bible: 'M5 4a1 1 0 0 1 1-1h13v18H6a1 1 0 0 1-1-1zM12 7v8M9 10h6',
  sprout: 'M12 21v-9M12 12c0-4 3-7 8-7 0 4-3 7-8 7zM12 14c0-3-2-5-6-5 0 3 2 5 6 5',
  image: 'M4 5h16v14H4zM4 16l5-5 4 4 2-2 5 5M15 9.5a1.5 1.5 0 1 0 0-.01',
  screen: 'M3 5h18v11H3zM8 20h8M12 16v4',
  bolt: 'M13 3 5 13h6l-1 8 8-10h-6z',
  chart: 'M4 20h16M7 16v-5M12 16V7M17 16v-8',
  baby: 'M12 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM7 21v-4a5 5 0 0 1 10 0v4M9 14l-3-2M15 14l3-2',
  pen: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  check: 'M4 12.5 9 17.5 20 6.5',
  church: 'M12 2v4M10 4h4M5 21V11l7-4 7 4v10M3 21h18M10 21v-4a2 2 0 0 1 4 0v4',
  people: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  ministry: 'M12 3v18M3 12h18M7 7l10 10M17 7 7 17',
  chat: 'M4 5h16v11H9l-5 4z',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 7.5h.01',
  meeting: 'M3 20v-1a4 4 0 0 1 4-4h2a4 4 0 0 1 4 4v1M8 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM15 14h2a4 4 0 0 1 4 4v2M16 5a3 3 0 0 1 0 6',
  wave: 'M7 11V6a2 2 0 0 1 4 0v5M11 6V4a2 2 0 0 1 4 0v7M15 7a2 2 0 0 1 4 0v6a7 7 0 0 1-7 7h-1a6 6 0 0 1-5.2-3L3.5 13a1.8 1.8 0 0 1 3-2L7 12M3 5l2 1M4 2l1.5 2',
  music: 'M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  globe: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.5 3.8 5.5 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.5-3.8-9S9.5 5.5 12 3z',
  drop: 'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z',
  flag: 'M5 21V4M5 4h12l-2.5 4L17 12H5',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  logout: 'M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 17l5-5-5-5M15 12H4',
  expand: 'M4 4h16v16H4zM9 4v16M13 9l3 3-3 3',
  collapse: 'M4 4h16v16H4zM9 4v16M16 9l-3 3 3 3',
  shield: 'M12 3l8 3v6c0 4.6-3.4 8.3-8 9-4.6-.7-8-4.4-8-9V6z',
  hand: 'M7 11V6a2 2 0 0 1 4 0v5M11 10V4a2 2 0 0 1 4 0v6M15 10V6a2 2 0 0 1 4 0v7a7 7 0 0 1-7 7h-1a6 6 0 0 1-5-3l-2.5-4a1.8 1.8 0 0 1 3-2L7 13',
};

export function Icon({ name }: { name: string }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name]} />
    </svg>
  );
}

export interface NavGroup {
  /** Nombre de la sección; null para la que no lleva título (Inicio). */
  title: string | null;
  items: NavItem[];
}

type Candidate = NavItem & { visible: boolean };

/** Opciones de navegación por sección según los permisos efectivos (solo UX: el backend decide). */
export function useNavGroups(): NavGroup[] {
  const me = useMe();
  const agenda = agendaTabs(me).map((t) => t.to);
  const visitors = useVisitorAccess(me.activeChurchId);
  const denomination = useVerifiedDenomination(me.activeChurchId);
  const ad = denomination === 'asambleas-de-dios';
  const overseer = useIsOverseer(me.personId);
  const home = useHomeEnabled();
  // Iglesia en vivo: solo si hay algo configurado; quien opera la transmisión la ve siempre.
  const operatesLive = me.can('church.manage') || me.can('presentation.operate');
  const liveConfigured = useLiveConfigured(me.activeChurchId, me.can('church.read') && !operatesLive);
  const groups: { title: string | null; items: Candidate[] }[] = [
    { title: null, items: [{ to: '/app', label: 'Inicio', short: 'Inicio', icon: 'home', visible: true }] },
    {
      // Lo de todos los días del miembro, en este orden. Sus ministerios y la Comunidad los arma la
      // página de Mi iglesia; las solicitudes (consejería, bautismos…) van en el botón flotante.
      title: 'Mi iglesia',
      items: [
        { to: '/app/cultos', label: 'Culto', short: 'Culto', icon: 'worship', visible: me.can('worship.read') },
        { to: '/app/salones', label: 'Raíces', short: 'Raíces', icon: 'book', visible: me.can('departments.read') },
        { to: '/app/calendario', label: 'Agenda', short: 'Agenda', icon: 'calendar', also: agenda, visible: me.can('calendar.read') },
        { to: '/app/chats', label: 'Chats', short: 'Chats', icon: 'chat', visible: me.can('church.read') },
        { to: '/app/oracion', label: 'Oración', short: 'Oración', icon: 'pray', visible: me.can('prayer.read') },
        // Todos leen los avisos; quien publica ve además «Nuevo aviso» en la página.
        { to: '/app/avisos', label: 'Avisos', short: 'Avisos', icon: 'megaphone', visible: me.can('communication.read') || me.can('communication.send') },
        // Quien vende (voluntarios de la cafetería) la encuentra aquí, sin entrar a Administración.
        { to: '/app/comercio', label: 'Cafetería y tienda', short: 'Cafetería', icon: 'cup', visible: me.can('commerce.sell') },
        // El equipo de cuna la usa cada domingo; los papás la encuentran en el botón «Solicitudes».
        { to: '/app/guarderia', label: 'Guardería', short: 'Guardería', icon: 'baby', visible: me.can('nursery.operate') },
        { to: '/app/en-vivo', label: 'Iglesia en vivo', short: 'En vivo', icon: 'screen', visible: me.can('church.read') && (operatesLive || liveConfigured) },
        { to: '/app/vocacion', label: 'Mi vocación', short: 'Vocación', icon: 'hand', also: ['/app/servicio', '/app/servir'], visible: me.can('church.read') },
        { to: '/app/nuestra-historia', label: 'Nuestra historia', short: 'Historia', icon: 'flag', visible: me.can('church.read') },
        { to: '/app/organigrama', label: 'Organigrama', short: 'Organigrama', icon: 'ministry', visible: me.can('departments.read') },
      ],
    },
    {
      // Para quien dirige: el directorio de ministerios y los miembros (los avisos viven en Mi iglesia).
      title: 'Dirigir',
      items: [
        { to: '/app/ministerios', label: 'Ministerios', short: 'Ministerios', icon: 'ministry', visible: me.can('departments.manage') },
        { to: '/app/miembros', label: 'Miembros', short: 'Miembros', icon: 'people', also: ['/app/personas', '/app/solicitudes', '/app/visitantes'], visible: me.can('members.read') || visitors.canUse },
      ],
    },
    {
      // Botón flotante «Solicitudes»: lo que se pide cuando se necesita, no todos los días.
      title: 'Solicitudes',
      items: [
        { to: '/app/consejeria', label: 'Consejería', short: 'Consejería', icon: 'heart', visible: me.can('church.read') },
        { to: '/app/ordenanzas', label: 'Bautismos', short: 'Bautismos', icon: 'drop', visible: me.can('church.read') },
        { to: '/app/ordenanzas?ver=presentaciones', label: 'Presentación de niños', short: 'Presentación', icon: 'baby', visible: me.can('church.read') },
        { to: '/app/mantenimiento', label: 'Mantenimiento', short: 'Mantenimiento', icon: 'wrench', visible: me.can('church.read') },
        // Alex (2026-11-02): todos los miembros tienen acceso fácil a la Guardería (papás, equipo y rol).
        { to: '/app/guarderia', label: 'Guardería', short: 'Guardería', icon: 'baby', visible: me.can('nursery.operate') || me.can('nursery.use') },
      ],
    },
    {
      title: 'Comunidad',
      items: [
        // LoveHome dentro de KEORA: la vida de la familia en casa, con la misma cuenta y familia.
        { to: '/app/hogar', label: 'Mi familia', short: 'Mi familia', icon: 'family', visible: home },
        { to: '/app/testimonios', label: 'Gratitud y testimonios', short: 'Gratitud', icon: 'heart', visible: me.can('church.read') },
        { to: '/app/foro', label: 'Foro', short: 'Foro', icon: 'chat', visible: me.can('church.read') },
        { to: '/app/encuestas', label: 'Encuestas', short: 'Encuestas', icon: 'check', visible: me.can('church.read') },
        { to: '/app/ayuda', label: 'Ayuda comunitaria', short: 'Ayuda', icon: 'hand', visible: me.can('church.read') },
        { to: '/app/misiones', label: 'Misiones', short: 'Misiones', icon: 'globe', visible: me.can('church.read') },
        { to: '/app/red', label: 'Red de iglesias', short: 'Red', icon: 'church', visible: me.can('church.read') },
        { to: '/app/campanas', label: 'Ayunos y campañas', short: 'Campañas', icon: 'sprout', visible: me.can('church.read') },
        { to: '/app/recaudaciones', label: 'Recaudaciones', short: 'Recaudar', icon: 'tag', visible: me.can('church.read') },
        { to: '/app/promesas', label: 'Promesas', short: 'Promesas', icon: 'coin', visible: me.canOwn('finance.read') },
        { to: '/app/monedero', label: 'Monedero', short: 'Monedero', icon: 'card', visible: me.can('church.read') },
      ],
    },
    {
      title: 'Palabra',
      items: [
        { to: '/app/llamado', label: 'Quiero conocerle', short: 'Conocerle', icon: 'heart', visible: true },
        { to: '/app/biblia', label: 'Biblia', short: 'Biblia', icon: 'bible', visible: true },
        { to: '/app/devocional', label: 'Planes de lectura', short: 'Planes', icon: 'calendar', visible: me.can('devotional.read') },
        { to: '/app/crecimiento', label: 'Mi crecimiento', short: 'Crecer', icon: 'sprout', visible: true },
        { to: '/app/cuaderno', label: 'Cuaderno de estudio', short: 'Cuaderno', icon: 'pen', visible: true },
        // El lápiz ya es del Cuaderno: el diario lleva las manos en oración.
        { to: '/app/oracion/diario', label: 'Mi diario de oración', short: 'Diario', icon: 'pray', visible: true },
        { to: '/app/biblioteca', label: 'Biblioteca', short: 'Biblioteca', icon: 'book', visible: me.can('church.read') },
        { to: '/app/publicaciones', label: 'Publicaciones', short: 'Publicaciones', icon: 'megaphone', visible: me.can('publishing.read') },
      ],
    },
    {
      title: 'Culto y medios',
      items: [
        { to: '/app/cantos', label: 'Cantos', short: 'Cantos', icon: 'music', visible: me.can('worship.manage') || me.can('presentation.read') },
        { to: '/app/contenido', label: 'Contenido', short: 'Contenido', icon: 'pen', visible: me.can('content.read') },
        { to: '/app/multimedia', label: 'Multimedia', short: 'Multimedia', icon: 'image', visible: me.can('media.read') },
        { to: '/app/presentaciones', label: 'Proyectar', short: 'Proyectar', icon: 'screen', visible: me.can('presentation.read') },
        { to: '/app/formularios', label: 'Formularios', short: 'Formularios', icon: 'form', visible: me.can('forms.read') },
      ],
    },
    {
      // Solo iglesias con la denominación verificada por la administración de KEORA.
      title: 'Asambleas de Dios',
      items: [
        // El informe lleva nombres de convertidos y bautizados: solo la administración de la iglesia.
        { to: '/app/asambleas/informe', label: 'Informe estadístico', short: 'Informe', icon: 'chart', visible: ad && me.can('church.manage') },
        { to: '/app/asambleas/credenciales', label: 'Credenciales ministeriales', short: 'Credenciales', icon: 'card', visible: ad && me.can('members.read') },
        { to: '/app/asambleas/ofrendas', label: 'Ofrendas misioneras', short: 'Misiones', icon: 'globe', visible: ad && me.can('finance.read') },
        { to: '/app/asambleas/escuela-dominical', label: 'Escuela Dominical', short: 'Dominical', icon: 'book', visible: ad && me.can('departments.read') },
        { to: '/app/asambleas/ebv', label: 'Escuela Bíblica de Vacaciones', short: 'EBV', icon: 'sprout', visible: ad && me.can('departments.read') },
        { to: '/app/asambleas/exploradores', label: 'Exploradores del Rey', short: 'Exploradores', icon: 'flag', visible: ad && me.can('departments.read') },
        { to: '/app/asambleas/misioneritas', label: 'Misioneritas', short: 'Misioneritas', icon: 'heart', visible: ad && me.can('departments.read') },
        { to: '/app/biblia/trivia', label: 'Trivia bíblica', short: 'Trivia', icon: 'check', visible: ad },
        { to: '/app/asambleas/presbiterio', label: 'Mi sección y distrito', short: 'Sección', icon: 'globe', visible: overseer },
        { to: '/app/asambleas/ministerios', label: 'Ministerios del Concilio', short: 'Concilio', icon: 'ministry', visible: ad && me.can('church.manage') },
      ],
    },
    {
      title: 'Administración',
      items: [
        { to: '/app/aprobaciones', label: 'Aprobaciones', short: 'Aprobar', icon: 'check', visible: me.can('approvals.decide') },
        { to: '/app/finanzas', label: 'Finanzas', short: 'Finanzas', icon: 'coin', visible: me.can('finance.read') },
        // Quien solo vende la tiene en Mi iglesia; aquí, quien administra la cafetería o la tienda.
        { to: '/app/comercio', label: 'Cafetería y tienda', short: 'Cafetería', icon: 'cup', visible: me.can('commerce.manage') },
        { to: '/app/inventario', label: 'Inventario', short: 'Inventario', icon: 'box', visible: me.can('inventory.read') },
        { to: '/app/reportes', label: 'Reportes', short: 'Reportes', icon: 'chart', visible: me.can('reports.read') },
        { to: '/app/sitio', label: 'Sitio público', short: 'Sitio', icon: 'globe', visible: me.can('church.manage') },
        { to: '/app/configuracion-iglesia', label: 'Configuración de la iglesia', short: 'Configurar', icon: 'gear', visible: me.can('church.manage') || me.can('automation.read') },
        { to: '/app/facturacion', label: 'Plan y pagos', short: 'Pagos', icon: 'card', visible: me.can('billing.manage') },
      ],
    },
    {
      title: 'Cuenta',
      items: [
        { to: '/app/perfil', label: 'Mi perfil', short: 'Perfil', icon: 'user', also: ['/app/iglesias/nueva'], visible: !me.isChild },
      ],
    },
    {
      title: 'Panel KEORA',
      items: [
        { to: '/app/admin/iglesias', label: 'Iglesias', short: 'Iglesias', icon: 'church', visible: me.isPlatformAdmin },
        { to: '/app/admin/usuarios', label: 'Usuarios', short: 'Usuarios', icon: 'people', visible: me.isPlatformAdmin },
        { to: '/app/planes', label: 'Planes', short: 'Planes', icon: 'tag', visible: me.isPlatformAdmin },
      ],
    },
  ];
  return groups
    .map((g) => ({ title: g.title, items: g.items.filter((i) => i.visible).map(({ visible: _visible, ...item }) => item) }))
    .filter((g) => g.items.length > 0);
}

/** El menú en tres partes: lo de todos los días, las secciones y Mi perfil. */
export interface MenuParts {
  /** Inicio y Mi familia: directo, sin entrar a una sección. */
  daily: NavItem[];
  /** Una opción por sección (abre su página con accesos grandes); si tiene una sola opción, va directa. */
  sections: NavItem[];
  profile: NavItem | null;
}

export function useMenuParts(): MenuParts {
  const groups = useNavGroups();
  const all = groups.flatMap((g) => g.items);
  const daily: NavItem[] = all.filter((i) => i.to === '/app' || i.to === '/app/hogar');
  const sections: NavItem[] = [];
  for (const hub of HUBS) {
    if (INSIDE_MY_CHURCH.includes(hub.title)) continue;
    // Mi iglesia siempre abre su página (avisos, Comunidad y mis ministerios), aunque tenga una sola opción.
    const rest = hubItems(groups, hub.title);
    if (hub.title === 'Mi iglesia' && rest.length > 0) {
      sections.push({ to: hub.to, label: hub.title, short: hub.short, icon: hub.icon, also: [...rest.flatMap((i) => [i.to, ...(i.also ?? [])]), '/app/comunidad'] });
      continue;
    }
    // Con una sola opción va directa, pero con el ícono de la sección (cada sección tiene el suyo).
    if (rest.length === 1) sections.push({ ...rest[0]!, icon: hub.icon });
    else if (rest.length > 1) sections.push({ to: hub.to, label: hub.title, short: hub.short, icon: hub.icon, also: rest.flatMap((i) => [i.to, ...(i.also ?? [])]) });
  }
  return { daily, sections, profile: all.find((i) => i.to === '/app/perfil') ?? null };
}

/** El menú corto completo, en orden: lo diario, las secciones y Mi perfil. */
export function useMenu(): NavItem[] {
  const { daily, sections, profile } = useMenuParts();
  return [...daily, ...sections, ...(profile ? [profile] : [])];
}

/** Las opciones de una sección (Mi familia va directo en el menú). */
export function hubItems(groups: NavGroup[], title: string): NavItem[] {
  return groups.find((g) => g.title === title)?.items.filter((i) => i.to !== '/app/hogar') ?? [];
}

/** Lo que va en el botón flotante «Solicitudes». */
export function useHelpItems(): NavItem[] {
  return useNavGroups().find((g) => g.title === 'Solicitudes')?.items ?? [];
}

/** Pestañas de la Agenda: lo que tiene fecha, en un solo lugar (Cultos tiene su propio botón). */
export function agendaTabs(me: ReturnType<typeof useMe>): { to: string; label: string }[] {
  return [
    { to: '/app/calendario', label: 'Calendario', visible: me.can('calendar.read') },
    { to: '/app/eventos', label: 'Eventos', visible: me.can('calendar.read') },
    { to: '/app/reuniones', label: 'Reuniones', visible: me.can('departments.read') },
    { to: '/app/campamentos', label: 'Campamentos', visible: me.can('church.read') },
  ]
    .filter((t) => t.visible)
    .map(({ to, label }) => ({ to, label }));
}

/** Todas las opciones visibles, en el orden del menú. */
export function useNavItems(): NavItem[] {
  return useNavGroups().flatMap((g) => g.items);
}

export const MORE_ITEM: NavItem = { to: '/app/mas', label: 'Más', short: 'Más', icon: 'more' };

/** Barra del celular: Inicio, Mi familia, Palabra, Mi iglesia y, para quien dirige, Dirigir; lo demás va en «Más». */
const BOTTOM = ['/app', '/app/hogar', 'Palabra', 'Mi iglesia', 'Dirigir'];

export function useBottomMenu(): { bar: NavItem[]; rest: NavItem[] } {
  const groups = useNavGroups();
  const menu = useMenu();
  const all = groups.flatMap((g) => g.items);
  // Una sección puede ir como su página o, si tiene una sola opción, como esa opción.
  const find = (key: string) => {
    if (key.startsWith('/')) return menu.find((i) => i.to === key) ?? all.find((i) => i.to === key);
    const hub = HUBS.find((h) => h.title === key);
    const rest = hubItems(groups, key);
    return menu.find((i) => i.to === hub?.to || (rest.length === 1 && i.to === rest[0]!.to));
  };
  const bar = BOTTOM.map(find).filter((i): i is NavItem => !!i);
  const rest = menu.filter((i) => !bar.includes(i));
  return { bar: rest.length ? [...bar, MORE_ITEM] : bar, rest };
}
